import React, { useState } from 'react';

import { Link } from 'react-router-dom';
import { ClickAwayListener, MenuItem, MenuList, Paper, Popper } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { ExpandMoreRounded } from '@material-ui/icons';

import { MAIN_MENU_ENTRIES } from '../constants/main-menu.constants';
import { MainMenuEntry } from '../types/main-menu-entry.type';
import { MainMenuLink } from '../types/main-menu-link.type';

type OpenEntry = {
  label: string;
  anchor: HTMLElement;
};

const useStyles = makeStyles((theme) => ({
  // Une rangée de liens est unidimensionnelle : le flex avec `gap` la gère mieux qu'une grille,
  // et le débordement horizontal reste maîtrisé sur les écrans étroits.
  nav: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '1rem',
    flexWrap: 'wrap',
  },
  entry: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.25rem',
    minHeight: 44,
    padding: '0 12px',
    border: 'none',
    background: 'none',
    font: 'inherit',
    fontSize: '0.9rem',
    color: '#1c1c1c',
    cursor: 'pointer',
    whiteSpace: 'nowrap',

    '&:hover': {
      color: theme.palette.client.primary.main,
    },
  },
  entryOpen: {
    color: theme.palette.client.primary.main,
    fontWeight: 600,
  },
  expandIcon: {
    fontSize: '1.1rem',
    transition: 'transform 120ms ease',
  },
  expandIconOpen: {
    transform: 'rotate(180deg)',
  },
  popper: {
    zIndex: theme.zIndex.appBar + 1,
  },
  dropdown: {
    minWidth: 200,
    marginTop: 4,
  },
  childLink: {
    color: '#1c1c1c',
    textDecoration: 'none',
    fontSize: '0.9rem',
  },
  '@media (prefers-reduced-motion: reduce)': {
    expandIcon: {
      transition: 'none',
    },
  },
}));

const MainMenu: React.FC = () => {
  const classes = useStyles();
  const [openEntry, setOpenEntry] = useState<OpenEntry | null>(null);

  const closeMenu = () => setOpenEntry(null);

  const toggleEntry = (entry: MainMenuEntry, target: HTMLElement) =>
    setOpenEntry((current) => (current?.label === entry.label ? null : { label: entry.label, anchor: target }));

  const renderChild = (child: MainMenuLink) => {
    // Sans destination, la sous-entrée reste affichée mais ne navigue pas : elle ne doit surtout
    // pas produire de lien mort.
    if (!child.path) {
      return (
        <MenuItem key={child.label} onClick={closeMenu} className={classes.childLink}>
          {child.label}
        </MenuItem>
      );
    }

    return (
      <MenuItem key={child.label} component={Link} to={child.path} onClick={closeMenu} className={classes.childLink}>
        {child.label}
      </MenuItem>
    );
  };

  const renderEntry = (entry: MainMenuEntry) => {
    const isOpen = openEntry?.label === entry.label;

    if (!entry.children) {
      if (!entry.path) {
        return (
          <button key={entry.label} type="button" className={classes.entry}>
            {entry.label}
          </button>
        );
      }

      return (
        <Link key={entry.label} to={entry.path} className={classes.entry}>
          {entry.label}
        </Link>
      );
    }

    return (
      <button
        key={entry.label}
        type="button"
        className={`${classes.entry} ${isOpen ? classes.entryOpen : ''}`}
        aria-haspopup="true"
        aria-expanded={isOpen}
        onClick={(event) => toggleEntry(entry, event.currentTarget)}
      >
        {entry.label}
        <ExpandMoreRounded className={`${classes.expandIcon} ${isOpen ? classes.expandIconOpen : ''}`} />
      </button>
    );
  };

  const openedEntry = MAIN_MENU_ENTRIES.find((entry) => entry.label === openEntry?.label);

  return (
    <ClickAwayListener onClickAway={closeMenu}>
      <nav
        className={classes.nav}
        aria-label="Menu principal"
        onKeyDown={(event) => {
          if (event.key === 'Escape') closeMenu();
        }}
      >
        {MAIN_MENU_ENTRIES.map(renderEntry)}

        {/* `disablePortal` garde le déroulant dans le `nav` : le clic sur une sous-entrée n'est
            alors pas vu comme un clic en dehors par le `ClickAwayListener`. */}
        <Popper
          open={Boolean(openEntry)}
          anchorEl={openEntry?.anchor}
          placement="bottom-start"
          className={classes.popper}
          disablePortal
        >
          <Paper className={classes.dropdown} elevation={3}>
            <MenuList dense>{openedEntry?.children?.map(renderChild)}</MenuList>
          </Paper>
        </Popper>
      </nav>
    </ClickAwayListener>
  );
};

export default MainMenu;
